import { useEffect, useRef, useState } from 'react';
import {
    Chart,
    BarController,
    BarElement,
    LineController,
    LineElement,
    PointElement,
    CategoryScale,
    LinearScale,
    Tooltip,
    Legend,
    Filler,
} from 'chart.js';
import WatchlistDashboardService, { ActivationCrossSummary, ActivationVelocity } from '../WatchlistDashboardService';

Chart.register(BarController, BarElement, LineController, LineElement, PointElement, CategoryScale, LinearScale, Tooltip, Legend, Filler);

/**
 * Pipeline & Growth Analytics (mockup lines 368-392 / chart configs 1531-1581).
 *   - Activation Pipeline — Stage Distribution: grouped bar, one series per active outcome,
 *     bars = current alumni count at each stage (Denied excluded, matching the mockup).
 *   - Stage Advance Velocity: line/area, one series per outcome, monthly FORWARD advances.
 *
 * Both charts share the same "active outcome" set (outcomes with ≥1 alumni in activation) so the
 * legends line up. Data is group-scoped and re-fetches when the campus filter changes.
 */

const FONT = 'Jost, sans-serif';

// Per-outcome colours (mockup palette; extended for the 2 outcomes the mockup didn't show).
const OUTCOME_COLOR: Record<string, string> = {
    hiring: '#2563eb',
    mentorship: '#0d9488',
    donor: '#d97706',
    at: '#7c3aed',
    am: '#db2777',
    ra: '#0891b2',
};
const colorFor = (outcome: string) => OUTCOME_COLOR[outcome] || '#64748b';
const rgba = (hex: string, a: number) => {
    const h = hex.replace('#', '');
    const r = parseInt(h.slice(0, 2), 16);
    const g = parseInt(h.slice(2, 4), 16);
    const b = parseInt(h.slice(4, 6), 16);
    return `rgba(${r},${g},${b},${a})`;
};

const PipelineAnalyticsSection = ({ instituteId, filterInstituteId }: { instituteId: string | number; filterInstituteId?: string | null }) => {
    const [distribution, setDistribution] = useState<ActivationCrossSummary | null>(null);
    const [velocity, setVelocity] = useState<ActivationVelocity | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const barRef = useRef<HTMLCanvasElement>(null);
    const lineRef = useRef<HTMLCanvasElement>(null);
    const barChart = useRef<Chart | null>(null);
    const lineChart = useRef<Chart | null>(null);

    useEffect(() => {
        setLoading(true);
        setError(null);
        Promise.all([
            WatchlistDashboardService.getActivationCrossSummary(instituteId, filterInstituteId),
            WatchlistDashboardService.getActivationVelocity(instituteId, { filterInstituteId }),
        ])
            .then(([dist, vel]) => {
                setDistribution(dist);
                setVelocity(vel);
            })
            .catch((err) => setError(err?.message || 'Failed to load pipeline analytics.'))
            .finally(() => setLoading(false));
    }, [instituteId, filterInstituteId]);

    // Outcomes with at least one alumni in activation — drives both charts (consistent legends).
    const activeKeys = (distribution?.outcomes || []).filter((o) => o.total_in_outcome > 0).map((o) => o.outcome);

    // ── Bar: stage distribution ──
    useEffect(() => {
        if (!barRef.current || !distribution) return;
        if (barChart.current) barChart.current.destroy();

        const active = distribution.outcomes.filter((o) => o.total_in_outcome > 0);
        if (active.length === 0) return;

        // Stage labels from the first outcome, excluding the "denied" stage (mockup shows 7).
        const stageDefs = active[0].stages.filter((s) => s.code !== 'denied');
        const labels = stageDefs.map((s) => s.label);
        const datasets = active.map((o) => {
            const byCode = Object.fromEntries(o.stages.map((s) => [s.code, s.count]));
            return {
                label: o.label,
                data: stageDefs.map((s) => byCode[s.code] || 0),
                backgroundColor: rgba(colorFor(o.outcome), 0.7),
                borderRadius: 4,
            };
        });

        barChart.current = new Chart(barRef.current, {
            type: 'bar',
            data: { labels, datasets },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                plugins: { legend: { labels: { font: { family: FONT, size: 11 }, boxWidth: 12 } } },
                scales: {
                    y: { beginAtZero: true, ticks: { font: { family: FONT, size: 11 } }, grid: { color: '#f1f5f9' } },
                    x: { ticks: { font: { family: FONT, size: 10 } }, grid: { display: false } },
                },
            },
        });
        return () => barChart.current?.destroy();
    }, [distribution]);

    // ── Line: stage advance velocity ──
    useEffect(() => {
        if (!lineRef.current || !velocity) return;
        if (lineChart.current) lineChart.current.destroy();

        // Show the same outcome set as the bar chart; if none active, fall back to all returned.
        const keys = activeKeys.length > 0 ? activeKeys : velocity.outcomes.map((o) => o.outcome);
        const series = velocity.outcomes.filter((o) => keys.includes(o.outcome));
        const datasets = series.map((o) => {
            const c = colorFor(o.outcome);
            return {
                label: o.label,
                data: o.data,
                borderColor: c,
                backgroundColor: rgba(c, 0.08),
                tension: 0.4,
                fill: true,
                pointRadius: 4,
            };
        });

        lineChart.current = new Chart(lineRef.current, {
            type: 'line',
            data: { labels: velocity.months.map((m) => m.label), datasets },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                plugins: { legend: { labels: { font: { family: FONT, size: 11 }, boxWidth: 12 } } },
                scales: {
                    y: { beginAtZero: true, ticks: { font: { family: FONT, size: 11 } }, grid: { color: '#f1f5f9' } },
                    x: { ticks: { font: { family: FONT, size: 11 } }, grid: { display: false } },
                },
            },
        });
        return () => lineChart.current?.destroy();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [velocity, distribution]);

    const hasData = (distribution?.outcomes || []).some((o) => o.total_in_outcome > 0);

    return (
        <div className="col-12">
            <div className="d-flex align-items-center gap-3 mt-4">
                <h3 className="font-18 font-600 text-dark-blue m-0">Pipeline &amp; Growth Analytics</h3>
                <p className="m-0 font-12 text-secondary-emphasis">Stage movement velocity and watchlist growth trends</p>
            </div>

            {error && !loading && <div className="alert alert-danger font-13 mt-2">{error}</div>}

            <div className="row g-3 row-cols-1 row-cols-md-2 mb-4 mt-1">
                <div className="col">
                    <div className="bg-white border rounded-4 p-4 shadow-sm h-100">
                        <div className="font-15 font-600 text-dark-blue mb-2">Activation Pipeline — Stage Distribution</div>
                        <div className="font-12 text-secondary-emphasis mb-3">
                            Current alumni count at each stage across the active Activation Watchlists · Value Zone (Qualified / Contributing)
                        </div>
                        {loading ? (
                            <div className="font-13 text-secondary-emphasis py-5 text-center">Loading…</div>
                        ) : hasData ? (
                            <div style={{ height: 240 }}><canvas ref={barRef} /></div>
                        ) : (
                            <div className="font-13 text-muted py-5 text-center">No alumni in activation yet.</div>
                        )}
                    </div>
                </div>

                <div className="col">
                    <div className="bg-white border rounded-4 p-4 shadow-sm h-100">
                        <div className="font-15 font-600 text-dark-blue mb-2">Stage Advance Velocity</div>
                        <div className="font-12 text-secondary-emphasis mb-3">
                            Monthly count of alumni advancing a stage{velocity ? ` — ${velocity.months[0]?.label} to ${velocity.months[velocity.months.length - 1]?.label}` : ''}
                        </div>
                        {loading ? (
                            <div className="font-13 text-secondary-emphasis py-5 text-center">Loading…</div>
                        ) : (
                            <div style={{ height: 240 }}><canvas ref={lineRef} /></div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PipelineAnalyticsSection;
